import { API_ERROR_CODE, isApiError, type ApiErrorCode } from '@/services/errors';

/**
 * Athlete-facing copy for known backend error codes. Keys are typed
 * `ApiErrorCode` so a backend rename causes a compile error here.
 * Anything not mapped falls through to the generic message — never
 * surface raw API slugs to the user.
 */
export const ERROR_COPY: Partial<Record<ApiErrorCode, string>> = {
  [API_ERROR_CODE.DealUnavailable]: "This deal isn't available right now.",
  [API_ERROR_CODE.DealFull]: 'This deal is full this month — it reopens on the 1st.',
  // /advance returns 404 when the Action is gone server-side. The
  // "no Action yet" case is silently handled by the click+advance
  // fallback in handleCtaPress, so seeing this copy means the Action
  // existed locally but vanished on the server (admin removed, scoped
  // out, etc). Avoid suggesting the user "start" something they
  // already did.
  [API_ERROR_CODE.NotFound]: 'This step is no longer available. Pull to refresh.',
  [API_ERROR_CODE.RateLimited]: 'Slow down — try again in a minute.',
  [API_ERROR_CODE.StepNotFound]: "We couldn't find that step. Reload and try again.",
  [API_ERROR_CODE.StepHasNoCta]: 'That step has no action you need to take.',
  [API_ERROR_CODE.StepOutOfOrder]: 'Finish the previous step first.',
  [API_ERROR_CODE.Unauthenticated]: 'Sign in again to continue.',
  [API_ERROR_CODE.NetworkError]: 'Check your connection and try again.',
  [API_ERROR_CODE.Timeout]: 'That took too long. Try again.',
};

/** A step button whose action this build doesn't know: the server is newer than the app. */
export const OUTDATED_APP_COPY = 'This step needs a newer version of MarketPryce. Update the app, then try again.';

export function userFacingError(err: unknown): string {
  if (isApiError(err)) {
    const copy = ERROR_COPY[err.code as ApiErrorCode];
    if (copy) return copy;
  }
  return "Couldn't continue. Try again.";
}
